import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Users, User, X } from 'lucide-react';
import { getText } from '@/translate/text';
import { AppLocalEnum } from '@/translate/text';
import WhitelistManagement from '../../../components/notificationCenter/whitelistManagement';
import { NotificationService } from '@/services/notificationService';
import { useNavigate } from 'react-router-dom';
import { getActionLabel, notification_actions } from '@/constants/notificationsActions';
import { EstimatedRecipientsBar } from '@/components/notificationCenter/EstimatedRecipientsBar';

const ReviewStep = ({
  formData,
  setFormData,
  AUDIENCE_OPTIONS,
  estimatedUsers,
  estimateLoading,
  estimateError,
}) => {
  const lang = localStorage.getItem('language') || AppLocalEnum.EN;
  const [openWhitelistManagement, setOpenWhitelistManagement] = useState(false);
  const navigate = useNavigate();

  const [imagePreview, setImagePreview] = useState(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!formData?.image) {
      setImagePreview(null);
      return;
    }

    // If image is already a URL string, use it directly
    if (typeof formData.image === 'string') {
      setImagePreview(formData.image);
      return;
    }

    // If image is a File object, create an object URL
    if (formData.image instanceof File) {
      const objectUrl = URL.createObjectURL(formData.image);
      setImagePreview(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [formData?.image]);

  const getCleanedFormData = () => {
    const cleaned = { ...formData };

    if (cleaned.language === 'en') {
      cleaned.title_ar = '';
      cleaned.message_ar = '';
    } else if (cleaned.language === 'ar') {
      cleaned.title_en = '';
      cleaned.message_en = '';
    }

    return cleaned;
  };

  const sendTestNotification = async (selectedUserIds) => {
    try {
      setFormData?.((prev) => ({
        ...prev,
        test_audience_list: selectedUserIds ?? [],
      }));

      const payload = getCleanedFormData();
      const response = await NotificationService.sendTestNotification({
        ...payload,
        test_audience_list: selectedUserIds ?? [],
      });
      setFormData?.((prev) => ({
        ...prev,
        notification_id: response.data._id,
      }));
      setOpenWhitelistManagement(false);
    } catch (error) {
      console.error('Send Test Notification error:', error);
    }
  };

  const sendNotification = async () => {
    if (isSending) return;
    setIsSending(true);
    try {
      const payload = getCleanedFormData();
      const response = await NotificationService.sendNotification({
        ...payload,
      });
      if (response.status === 200 || response.status === 201) {
        setFormData({
          audience_type: 'single_users',
          subscription_status: '',
          region: '',
          gender: '',
          age_group: '',
          target_condition: '',
          activity_status: '',
          audience_list: [],
          test_audience_list: [],
          scheduling_type: 'instant',
          language: 'both',
          title_en: '',
          title_ar: '',
          message_en: '',
          message_ar: '',
          image: null,
          cta: notification_actions.HOME_SCREEN.id,
          cta_url: '',
          cta_offer_id: '',
          cta_ad_id: '',
          cta_category_id: '',
          schedule_date: '',
          schedule_time: '',
          notification_id: null,
        });
        navigate('/notification-center');
      }
    } catch (error) {
      console.error('Send Notification error:', error);
    } finally {
      setIsSending(false);
    }
  };
  const ctaLabel = getActionLabel(formData.cta);

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    const [y, m, d] = dateStr.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return date.toLocaleDateString(lang === AppLocalEnum.AR ? 'ar-EG' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTimeLabel = (timeStr) => {
    if (!timeStr) {
      return new Date().toLocaleTimeString(
        lang === AppLocalEnum.AR ? AppLocalEnum.AR : AppLocalEnum.EN_US,
        { hour: '2-digit', minute: '2-digit', hour12: true }
      );
    }
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours));
    date.setMinutes(parseInt(minutes));
    return date.toLocaleTimeString(
      lang === AppLocalEnum.AR ? AppLocalEnum.AR : AppLocalEnum.EN_US,
      { hour: '2-digit', minute: '2-digit', hour12: true }
    );
  };

  const displayDate =
    formData.scheduling_type === 'scheduled' && formData.schedule_date
      ? formatDateLabel(formData.schedule_date)
      : new Date().toISOString().split('T')[0];

  const displayTime =
    formData.scheduling_type === 'scheduled' && formData.schedule_time
      ? formatTimeLabel(formData.schedule_time)
      : new Date().toLocaleTimeString(
          lang === AppLocalEnum.AR ? AppLocalEnum.AR : AppLocalEnum.EN_US,
          { hour: '2-digit', minute: '2-digit', hour12: true }
        );

  const [actionsTarget, setActionsTarget] = useState(() => {
    if (typeof document === 'undefined') return null;
    return document.getElementById('review-step-actions');
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setActionsTarget(document.getElementById('review-step-actions'));
  }, []);

  const actions = (
    <>
      {/* Buttons */}
      {/* line */}
      <div className="flex justify-start mt-4">
        <div className="w-full h-[1px] bg-gray-300 dark:bg-gray-700"></div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button
          type="button"
          disabled={isSending}
          className={`bg-white w-44 py-2 border border-gray-100 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-semibold transition-all ${
            isSending ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={() => setOpenWhitelistManagement(true)}
        >
          {getText('send_test') || 'Send test'}
        </button>
        <button
          type="button"
          disabled={isSending}
          className={`w-44 py-2 rounded-xl text-white flex items-center justify-center gap-2 dark:text-gray-200 text-sm font-medium transition-all ${
            isSending
              ? 'bg-[#008236] opacity-70 cursor-not-allowed'
              : 'bg-[#00A63E] hover:bg-[#008236] dark:hover:bg-[#00A63E]'
          }`}
          onClick={sendNotification}
        >
          {isSending ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              {getText('sending') || 'Sending...'}
            </>
          ) : formData.scheduling_type === 'instant' ? (
            getText('send_now')
          ) : (
            getText('save')
          )}
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className=" px-4 py-3 bg-white rounded-2xl ">
        {/* Content Summary */}
        <span className="text-sm font-normal text-gray-500 tracking-wider">
          {getText('notification_content')}
        </span>
        <div className="grid grid-cols-2 gap-5 items-start my-2">
          <div className="col-span-1">
            {formData.title_en && formData.message_en && (
              <div className="mb-4">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {formData.title_en || formData.title_ar}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed">
                  {formData.message_en || formData.message_ar}
                </p>
              </div>
            )}
            {formData.title_ar && formData.message_ar && (
              <div className="mb-4" dir="rtl">
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {formData.title_ar}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed">
                  {formData.message_ar}
                </p>
              </div>
            )}
          </div>

          {imagePreview && (
            <div className="col-span-1 flex justify-end me-4">
              <div className=" w-52 h-36 rounded-2xl overflow-hidden shadow-md bg-gray-100">
                <img src={imagePreview} alt="Notification" className="w-full h-full object-cover" />
              </div>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="flex flex-col gap-1 mb-4">
          <span className="text-sm font-normal text-gray-500 tracking-wider">
            {getText('call_to_action')}
          </span>
          <div className="text-lg text-gray-700 dark:text-gray-300">
            {ctaLabel}
            {formData.cta === notification_actions.EXTERNAL_URL.id && formData.cta_url && (
              <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 break-all font-normal">
                ({formData.cta_url})
              </span>
            )}
            {formData.cta_offer_id &&
              formData.cta === notification_actions.OFFER_DETAILS_SCREEN.id && (
                <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 break-all font-normal">
                  (Offer ID: {formData.cta_offer_id})
                </span>
              )}
            {formData.cta_ad_id &&
              formData.cta === notification_actions.OFFER_DETAILS_SCREEN.id && (
                <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 break-all font-normal">
                  (Ad ID: {formData.cta_ad_id})
                </span>
              )}
            {formData.cta_category_id &&
              formData.cta === notification_actions.OFFER_CATEGORY_SCREEN.id && (
                <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 break-all font-normal">
                  (ID: {formData.cta_category_id})
                </span>
              )}
          </div>
        </div>

        {/* Meta Grid */}
        <div className="grid grid-cols-2 gap-8">
          {/* Left Column: Audience and Recipients */}
          <div className="space-y-6">
            <div className="space-y-1">
              <p className="text-sm font-normal text-gray-500">{getText('audience')}</p>
              <span className="inline-flex px-3 py-1 bg-[#F0FDF4] text-[#008236] dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg text-xs font-medium">
                {formData.audience_type === 'single_users'
                  ? getText('single_users')
                  : getText('group_users')}
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-normal text-gray-500">{getText('sent_date')}</p>
              <p className="text-lg text-gray-900 dark:text-gray-100">{displayDate}</p>
            </div>
          </div>

          {/* Right Column: Sent Date and Sent Time */}
          <div className="space-y-6">
            <div className="space-y-1">
              <p className="text-sm font-normal text-gray-500">{getText('recipients')}</p>
              <p className="text-lg text-gray-900 dark:text-gray-100">
                {formData.audience_type === 'single_users'
                  ? formData.audience_list?.length || 0
                  : (estimatedUsers ?? 0)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-normal text-gray-500">{getText('sent_time')}</p>
              <p className="text-lg text-gray-900 dark:text-gray-100">{displayTime}</p>
            </div>
          </div>
        </div>

        {/* Estimated Recipients Box Details */}
        <div className="mt-12">
          <EstimatedRecipientsBar
            formData={formData}
            audienceOptions={AUDIENCE_OPTIONS}
            estimatedUsers={estimatedUsers}
            estimateLoading={estimateLoading}
            estimateError={estimateError}
            enableRemoveFilter={false}
          />
        </div>
      </div>
      {actionsTarget ? createPortal(actions, actionsTarget) : actions}
      <WhitelistManagement
        isOpen={openWhitelistManagement}
        onClose={() => setOpenWhitelistManagement(false)}
        onSendTest={sendTestNotification}
      />
    </>
  );
};

export default ReviewStep;
